import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import axios from '../api/axios';
import './ExternalExpertScoringPage.css';

// part id = part_number (integer), per backend instruction
function computeAvg(parts) {
  const scored = parts.filter((p) => p.score !== null && p.score !== undefined);
  if (!scored.length) return null;
  return scored.reduce((s, p) => s + p.score, 0) / scored.length;
}

/* ===========================================================
   TooltipBubble — сам поповер, отрисован в document.body через портал,
   position: fixed по реальным экранным координатам якоря. Меряет
   собственный размер после рендера и зажимает (clamp) себя внутри
   вьюпорта по X, а по Y переворачивается (top<->bottom), если места
   не хватает — поэтому не обрезается родительским overflow:hidden
   и никогда не "уходит" за край узкого сайдбара/карточки.
=========================================================== */
function TooltipBubble({ text, anchorRect, position }) {
  const bubbleRef = useRef(null);
  const [coords, setCoords] = useState(null);

  useLayoutEffect(() => {
    const el = bubbleRef.current;
    if (!el || !anchorRect) return;
    const margin = 8;
    const rect = el.getBoundingClientRect();
    const centerX = anchorRect.left + anchorRect.width / 2;

    let left = centerX - rect.width / 2;
    left = Math.max(margin, Math.min(left, window.innerWidth - rect.width - margin));

    let top;
    let finalPosition = position;
    if (position === 'top') {
      top = anchorRect.top - rect.height - 8;
      if (top < margin) { top = anchorRect.bottom + 8; finalPosition = 'bottom'; }
    } else {
      top = anchorRect.bottom + 8;
      if (top + rect.height > window.innerHeight - margin) {
        top = anchorRect.top - rect.height - 8;
        finalPosition = 'top';
      }
    }

    setCoords({ top, left, arrowLeft: centerX - left, position: finalPosition });
  }, [anchorRect, position, text]);

  return createPortal(
    <span
      ref={bubbleRef}
      role="tooltip"
      style={{
        position: 'fixed',
        top: coords ? coords.top : -9999,
        left: coords ? coords.left : -9999,
        visibility: coords ? 'visible' : 'hidden',
        background: '#0f172a', color: '#f8fafc',
        fontSize: 12, fontWeight: 500, lineHeight: 1.4,
        padding: '7px 10px', borderRadius: 8,
        whiteSpace: 'pre-line', maxWidth: 'min(260px, calc(100vw - 16px))',
        width: 'max-content',
        boxShadow: '0 8px 24px rgba(15,23,42,0.25)',
        pointerEvents: 'none', zIndex: 10000,
      }}
    >
      {text}
      {coords && (
        <span
          style={{
            position: 'absolute',
            left: Math.max(10, Math.min(coords.arrowLeft, (bubbleRef.current?.offsetWidth || 260) - 10)),
            transform: 'translateX(-50%) rotate(45deg)',
            width: 8, height: 8, background: '#0f172a',
            ...(coords.position === 'top' ? { bottom: -4 } : { top: -4 }),
          }}
        />
      )}
    </span>,
    document.body
  );
}

/* ===========================================================
   InstantTip — свой тултип без браузерной задержки. Показывается
   ровно в момент onMouseEnter, скрывается на onMouseLeave.
   Координаты якоря берутся через getBoundingClientRect, а сам поповер
   рендерится в портал — не зависит от overflow/ширины родителя.
   position="top" | "bottom" — предпочитаемая сторона (авто-флип, если
   места не хватает — см. TooltipBubble).
=========================================================== */
function InstantTip({ text, children, position = 'top' }) {
  const [anchorRect, setAnchorRect] = useState(null);
  const anchorRef = useRef(null);

  if (!text) return children;

  const show = () => {
    const el = anchorRef.current;
    if (!el) return;
    setAnchorRect(el.getBoundingClientRect());
  };
  const hide = () => setAnchorRect(null);

  return (
    <span
      ref={anchorRef}
      style={{ display: 'inline-flex' }}
      onMouseEnter={show}
      onMouseLeave={hide}
    >
      {children}
      {anchorRect && <TooltipBubble text={text} anchorRect={anchorRect} position={position} />}
    </span>
  );
}

/* ===========================================================
   DescriptorInfo — значок "!" с текстом-пояснением к критерию
   в целом. Рендерится только если есть непустой descriptor.
=========================================================== */
function DescriptorInfo({ text }) {
  if (!text || !String(text).trim()) return null;
  return (
    <InstantTip text={text} position="bottom">
      <span
        className="eex-descriptor-info"
        onClick={(e) => e.stopPropagation()}
        style={{
          display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
          width: 18, height: 18, borderRadius: '50%', flexShrink: 0,
          background: '#eff6ff', color: '#2563eb', border: '1px solid #bfdbfe',
          fontSize: 11, fontWeight: 800, fontStyle: 'normal', cursor: 'help',
          lineHeight: 1,
        }}
      >
        !
      </span>
    </InstantTip>
  );
}

/* ScorePip — необязательный `hint` показывается через InstantTip (без
   браузерной задержки), только если для этого балла есть текст индикатора. */
function ScorePip({ value, selected, onClick, disabled, hint }) {
  const active = selected === value;
  const pip = (
    <button
      onClick={() => !disabled && onClick(value)}
      disabled={disabled}
      className={`eex-score-pip eex-score-pip--${value}${active ? ' eex-score-pip--active' : ''}${hint ? ' eex-score-pip--has-hint' : ''}`}
    >
      {value}
    </button>
  );
  if (!hint) return pip;
  return <InstantTip text={hint} position="top">{pip}</InstantTip>;
}

function PartRow({ part, index, onScore, onRemove, disabled, locale, indicatorsMap }) {
  const label = locale === 'ru' ? `Часть ${index + 1}` : `Бөлім ${index + 1}`;
  return (
    <div className="eex-part-row">
      <span className="eex-part-label">{label}</span>
      <div className="eex-part-pips">
        {[0, 1, 2, 3].map((v) => (
          <ScorePip
            key={v}
            value={v}
            selected={part.score}
            onClick={(s) => onScore(part.id, s)}
            disabled={disabled}
            hint={indicatorsMap?.get(v) || null}
          />
        ))}
      </div>
      {!disabled && (
        <button className="eex-part-remove" onClick={() => onRemove(part.id)}>×</button>
      )}
    </div>
  );
}

function AvgDisplay({ parts, locale }) {
  const val = computeAvg(parts);
  const label = locale === 'ru' ? 'Средний балл' : 'Орташа балл';
  if (val === null) return null;
  const pct = val / 3;
  const colorClass = pct < 0.4 ? 'eex-avg--red' : pct < 0.7 ? 'eex-avg--yellow' : 'eex-avg--green';
  return (
    <div className={`eex-avg-display ${colorClass}`}>
      <span className="eex-avg-label">{label}</span>
      <div className="eex-avg-right">
        <div className="eex-avg-bar-track">
          <div className="eex-avg-bar-fill" style={{ width: `${(pct * 100).toFixed(1)}%` }} />
        </div>
        <span className="eex-avg-value">
          {Number.isInteger(val) ? val : val.toFixed(2)}
          <span className="eex-avg-max">/3</span>
        </span>
      </div>
    </div>
  );
}

export default function ExternalExpertScoringPage({ locale = 'kz', applicationId, onBack, saveRef }) {
  const id = applicationId;

  const [criteriaGroups, setCriteriaGroups] = useState([]);
  const [loading, setLoading]               = useState(true);
  const [submitting, setSubmitting]         = useState(false);
  const [readonly, setReadonly]             = useState(false);
  const [activeCatIdx, setActiveCatIdx]     = useState(0);
  const [activeCritId, setActiveCritId]     = useState(null);
  const [generalComment, setGeneralComment] = useState('');
  const [showGeneral, setShowGeneral]       = useState(false);
  const [saveFlash, setSaveFlash]           = useState(false);

  const token = localStorage.getItem('token');

  const t = useMemo(() => ({
    kz: {
      general_comment_title: 'Жалпы пікір',
      general_comment_placeholder: 'Кітап туралы жалпы пікіріңізді жазыңыз...',
      longCommentPlaceholder: 'Толық түсіндірме жазыңыз (мысалы: [photo1])...',
      save: 'Жобаны сақтау', finish: 'Аяқтау', comment: 'Ескерту...',
      long_comment: 'Толық түсіндірме', uploadPhoto: '+ Фото', deleteImage: 'Жою', back: 'Артқа',
      no_score: '⚠ Бағаланбаған критерий бар', showImages: 'Суреттер', hideImages: 'Жасыру',
      addPart: '+ Бөлім қосу', prev: '← Алдыңғы', next: 'Келесі →', done: 'Дайын ✓',
      progress: 'Прогресс', saved: 'Сақталды ✓', allDone: 'Барлығы толтырылды',
      of: '/', generalTab: '✎ Жалпы', loadingText: 'Жүктелуде…',
    },
    ru: {
      general_comment_title: 'Общий комментарий',
      general_comment_placeholder: 'Напишите общий комментарий о книге...',
      longCommentPlaceholder: 'Подробный комментарий (например: [photo1])...',
      save: 'Сохранить черновик', finish: 'Завершить', comment: 'Комментарий...',
      long_comment: 'Подробный комментарий', uploadPhoto: '+ Фото', deleteImage: 'Удалить', back: 'Назад',
      no_score: '⚠ Есть неоценённые критерии', showImages: 'Фото', hideImages: 'Скрыть',
      addPart: '+ Добавить часть', prev: '← Назад', next: 'Вперёд →', done: 'Готово ✓',
      progress: 'Прогресс', saved: 'Сохранено ✓', allDone: 'Все заполнены',
      of: 'из', generalTab: '✎ Итог', loadingText: 'Загрузка…',
    },
  }[locale]), [locale]);

  const MSG = {
    saveError:  { kz: 'Қате шықты', ru: 'Произошла ошибка' },
    finishAsk:  { kz: 'Бағалауды аяқтағыңыз келе ме?', ru: 'Завершить оценивание?' },
    finishDone: { kz: 'Бағалау аяқталды!', ru: 'Оценивание завершено!' },
    fetchError: { kz: 'Мәліметтерді алу мүмкін болмады.', ru: 'Не удалось получить данные.' },
    imgUploadOk:  (tag) => ({ kz: `Сурет жүктелді: ${tag}`, ru: `Фото загружено: ${tag}` }),
    imgUploadErr: { kz: 'Суретті жүктеу кезінде қате шықты', ru: 'Ошибка при загрузке изображения' },
  };

  const bi  = (msg) => { const m = typeof msg === 'function' ? msg() : msg; alert(`${m.kz}\n${m.ru}`); };
  const loc = (msg) => { const m = typeof msg === 'function' ? msg() : msg; alert(locale === 'ru' ? m.ru : m.kz); };

  useEffect(() => { if (id) fetchScoring(); }, [id]);

  const fetchScoring = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`/expertise/external/applications/${id}/scoring`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const newData = (res.data?.data || []).map((g) => ({
        ...g,
        criterias: (g.criterias || []).map((c) => {
          const rawJson = c.scores_json;
          const restoredParts = Array.isArray(rawJson) && rawJson.length > 0
            ? rawJson.map((p) => ({ id: p.part_number, score: p.score != null ? Number(p.score) : null }))
            : [];
          return { ...c, parts: restoredParts, error: null };
        }),
      }));
      const allStatuses = newData.flatMap((g) => g.criterias.map((c) => c.status));
      if (allStatuses.length > 0 && allStatuses.every((s) => s === 'аяқталды')) setReadonly(true);
      setCriteriaGroups(newData);
      const gc = res.data?.meta?.expert_comment ?? res.data?.general_comment ?? res.data?.comment
        ?? res.data?.relation?.comment ?? res.data?.meta?.general_comment ?? '';
      setGeneralComment(gc || '');
      for (let gi = 0; gi < newData.length; gi++) {
        for (const c of newData[gi].criterias) {
          if (c.score === null || c.score === '' || c.score === undefined) {
            setActiveCatIdx(gi); setActiveCritId(c.id); return;
          }
        }
      }
    } catch { loc(MSG.fetchError); }
    finally { setLoading(false); }
  };

  const allCriteria = useMemo(() =>
    criteriaGroups.flatMap((g, gi) => g.criterias.map((c, ci) => ({ ...c, catIdx: gi, critIdx: ci }))),
    [criteriaGroups]
  );
  const totalCriteria = allCriteria.length;
  const scoredCount   = allCriteria.filter((c) => c.score !== '' && c.score !== null && c.score !== undefined).length;
  const progressPct   = totalCriteria > 0 ? Math.round((scoredCount / totalCriteria) * 100) : 0;
  const currentGroup  = criteriaGroups[activeCatIdx];

  const goToFlat = (idx) => {
    if (idx < 0 || idx >= allCriteria.length) return;
    const c = allCriteria[idx]; setActiveCatIdx(c.catIdx); setActiveCritId(c.id);
  };

  const handleChange = (catIdx, critIdx, field, value) => {
    if (readonly) return;
    setCriteriaGroups((prev) => prev.map((g, gi) => gi !== catIdx ? g : {
      ...g, criterias: g.criterias.map((c, ci) => ci !== critIdx ? c : { ...c, [field]: value }),
    }));
  };

  const handleAddPart = (catIdx, critIdx) => {
    if (readonly) return;
    setCriteriaGroups((prev) => {
      const existing = prev[catIdx].criterias[critIdx].parts || [];
      const nextId = existing.length > 0 ? Math.max(...existing.map((p) => p.id)) + 1 : 1;
      const nextParts = [...existing, { id: nextId, score: null }];
      return prev.map((g, gi) => gi !== catIdx ? g : {
        ...g, criterias: g.criterias.map((c, ci) => ci !== critIdx ? c : { ...c, parts: nextParts }),
      });
    });
  };

  const handleScorePart = (catIdx, critIdx, partId, score) => {
    if (readonly) return;
    setCriteriaGroups((prev) => {
      const nextParts = (prev[catIdx].criterias[critIdx].parts || []).map((p) =>
        p.id === partId ? { ...p, score: p.score === score ? null : score } : p
      );
      const avgVal = computeAvg(nextParts);
      return prev.map((g, gi) => gi !== catIdx ? g : {
        ...g, criterias: g.criterias.map((c, ci) => ci !== critIdx ? c : {
          ...c, parts: nextParts, score: avgVal === null ? null : parseFloat(avgVal.toFixed(2)),
        }),
      });
    });
  };

  const handleRemovePart = (catIdx, critIdx, partId) => {
    if (readonly) return;
    setCriteriaGroups((prev) => {
      const nextParts = (prev[catIdx].criterias[critIdx].parts || []).filter((p) => p.id !== partId);
      const avgVal = computeAvg(nextParts);
      return prev.map((g, gi) => gi !== catIdx ? g : {
        ...g, criterias: g.criterias.map((c, ci) => ci !== critIdx ? c : {
          ...c, parts: nextParts, score: avgVal === null ? null : parseFloat(avgVal.toFixed(2)),
        }),
      });
    });
  };







  const saveDraft = async (suppressAlert = false) => {
    const scores = criteriaGroups.flatMap((cat) => cat.criterias.map((c) => ({
      criteria_id: c.id, score: c.score, comment: c.comment, long_comment: c.long_comment,
    })));
    const partsPayload = criteriaGroups.flatMap((cat) =>
      cat.criterias.filter((c) => Array.isArray(c.parts) && c.parts.length > 0).map((c) => ({
        criteria_id: c.id, avg_score: c.score,
        parts: c.parts.map((p) => ({ part_number: p.id, score: p.score })),
      }))
    );
    try {
      setSubmitting(true);
      if (partsPayload.length > 0) {
        await axios.post(`/expertise/external/applications/${id}/scoring/parts`,
          { parts: partsPayload }, { headers: { Authorization: `Bearer ${token}` } });
      }
      await axios.post(`/expertise/external/applications/${id}/scoring`,
        { scores, general_comment: generalComment }, { headers: { Authorization: `Bearer ${token}` } });
      if (!suppressAlert) { setSaveFlash(true); setTimeout(() => setSaveFlash(false), 2000); }
      return true;
    } catch { bi(MSG.saveError); return false; }
    finally { setSubmitting(false); }
  };

  const handleSave = () => saveDraft(false);

  useEffect(() => {
  if (saveRef) saveRef.current = () => saveDraft(false);
}, [criteriaGroups, generalComment]);

  const handleSaveAndFinish = async () => {
    const incomplete = criteriaGroups.flatMap((g) =>
      g.criterias.filter((c) => c.score === null || c.score === '' || c.score === undefined)
    );
    if (incomplete.length > 0) { alert(t.no_score); return; }
    if (!window.confirm(locale === 'ru' ? MSG.finishAsk.ru : MSG.finishAsk.kz)) return;
    const ok = await saveDraft(true);
    if (!ok) return;
    try {
      setSubmitting(true);
      await axios.post(`/expertise/external/applications/${id}/scoring/finish`,
        { general_comment: generalComment }, { headers: { Authorization: `Bearer ${token}` } });
      bi(MSG.finishDone); onBack && onBack();
    } catch { bi(MSG.saveError); }
    finally { setSubmitting(false); }
  };

  if (loading) return (
    <div className="eex-loading"><div className="eex-spinner" /><span>{t.loadingText}</span></div>
  );

  return (
    <div className="eex-root">
      {onBack && <button onClick={onBack} className="eex-btn-back">← {t.back}</button>}

      <div className="eex-progress-bar-wrap">
        <div className="eex-progress-bar-track">
          <div className="eex-progress-bar-fill" style={{ width: `${progressPct}%` }} />
        </div>
        <div className="eex-progress-meta">
          <span className="eex-progress-label">{t.progress}</span>
          <span className="eex-progress-count">{scoredCount} {t.of} {totalCriteria}</span>
          <span className="eex-progress-pct">{progressPct}%</span>
        </div>
      </div>

      <div className="eex-body">
        <nav className="eex-nav">
          {criteriaGroups.map((group, gi) => {
            const gs = group.criterias.filter((c) => c.score !== '' && c.score !== null && c.score !== undefined).length;
            const gt = group.criterias.length;
            return (
              <div key={gi} className={`eex-nav-group${gi === activeCatIdx && !showGeneral ? ' eex-nav-group--active' : ''}`}>
                <button className="eex-nav-group-btn" onClick={() => { setActiveCatIdx(gi); setShowGeneral(false); setActiveCritId(null); }}>
                  <span className="eex-nav-group-name">{group.category}</span>
                  <span className={`eex-nav-group-badge${gs === gt && gt > 0 ? ' eex-nav-group-badge--done' : ''}`}>{gs}/{gt}</span>
                </button>
              </div>
            );
          })}
          <div className={`eex-nav-group${showGeneral ? ' eex-nav-group--active' : ''}`}>
            <button className="eex-nav-group-btn" onClick={() => { setShowGeneral(true); setActiveCritId(null); }}>
              <span className="eex-nav-group-name">{t.generalTab}</span>
            </button>
          </div>
        </nav>

        <div className="eex-main">
          {showGeneral && (
            <div className="eex-card eex-card--general">
              <div className="eex-card-header">
                <span className="eex-card-cat">{t.general_comment_title}</span>
                {scoredCount === totalCriteria && totalCriteria > 0 && (
                  <span className="eex-all-done-badge">✓ {t.allDone}</span>
                )}
              </div>
              <div className="eex-field-block">
                <textarea className="eex-textarea eex-textarea--general" value={generalComment}
                  onChange={(e) => setGeneralComment(e.target.value)}
                  placeholder={t.general_comment_placeholder} readOnly={readonly || submitting} rows={7} />
              </div>
              <div className="eex-summary">
                {criteriaGroups.map((group, gi) => {
                  const gs = group.criterias.filter((c) => c.score !== '' && c.score !== null && c.score !== undefined).length;
                  const gt = group.criterias.length;
                  const total = group.criterias.reduce((s, c) => { const v = parseFloat(c.score); return s + (isNaN(v) ? 0 : v); }, 0);
                  return (
                    <div key={gi} className="eex-summary-row">
                      <span className="eex-summary-cat">{group.category}</span>
                      <span className="eex-summary-scored">{gs}/{gt}</span>
                      <span className="eex-summary-total">{total.toFixed(2)}</span>
                    </div>
                  );
                })}
              </div>
              {!readonly && (
                <div className="eex-action-btns">
                  <button className="eex-btn-save" onClick={handleSave} disabled={submitting}>{submitting ? '…' : t.save}</button>
                  <button className="eex-btn-finish" onClick={handleSaveAndFinish} disabled={submitting}>{submitting ? '…' : t.finish}</button>
                </div>
              )}
            </div>
          )}

          {!showGeneral && currentGroup && (
            <div className="eex-criteria-grid">
              {currentGroup.criterias.map((crit, ci) => {
                const scored   = crit.score !== '' && crit.score !== null && crit.score !== undefined;
                const isActive = activeCritId === crit.id;
                const myFlat   = allCriteria.findIndex((c) => c.id === crit.id);
                const parts    = crit.parts || [];

                // индикаторы приходят как [{score, text}] — раскладываем в карту score -> text,
                // пропуская записи без текста (text: null)
                const indicatorsMap = new Map(
                  (Array.isArray(crit.indicators) ? crit.indicators : [])
                    .filter((ind) => ind && ind.text)
                    .map((ind) => [Number(ind.score), ind.text])
                );

                return (
                  <div key={crit.id} className={`eex-crit-row${isActive ? ' eex-crit-row--active' : ''}${scored && !isActive ? ' eex-crit-row--done' : ''}`}>
                    <button className="eex-crit-row-header" onClick={() => { setActiveCritId((p) => p === crit.id ? null : crit.id); setShowGeneral(false); }}>
                      <span className="eex-crit-num-badge">{scored && !isActive ? '✓' : ci + 1}</span>
                      <span className="eex-crit-row-text" style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                        {crit.text}
                        <DescriptorInfo text={crit.descriptor} />
                      </span>
                      <span className="eex-crit-row-score">{scored ? `${crit.score}/${crit.max_point}` : `—/${crit.max_point}`}</span>
                      <span className="eex-crit-chevron">{isActive ? '▲' : '▼'}</span>
                    </button>

                    {isActive && (
                      <div className="eex-crit-row-body">
                        <div className="eex-parts-block">
                          {parts.map((part, pi) => (
                            <PartRow key={part.id} part={part} index={pi}
                              onScore={(partId, score) => handleScorePart(activeCatIdx, ci, partId, score)}
                              onRemove={(partId) => handleRemovePart(activeCatIdx, ci, partId)}
                              disabled={readonly || submitting} locale={locale}
                              indicatorsMap={indicatorsMap} />
                          ))}
                          {!readonly && (
                            <button className="eex-btn-add-part" onClick={() => handleAddPart(activeCatIdx, ci)} disabled={submitting}>
                              {t.addPart}
                            </button>
                          )}
                          <AvgDisplay parts={parts} locale={locale} />
                        </div>

                        <div className="eex-field-block">
                          <label className="eex-field-label">{t.comment}</label>
                          <textarea className="eex-textarea" value={crit.comment ?? ''}
                            onChange={(e) => handleChange(activeCatIdx, ci, 'comment', e.target.value)}
                            placeholder={t.comment} readOnly={readonly || submitting} rows={2} />
                        </div>

                        <div className="eex-nav-btns">
                          {myFlat > 0 && <button className="eex-btn-nav" onClick={() => goToFlat(myFlat - 1)}>{t.prev}</button>}
                          {myFlat < totalCriteria - 1
                            ? <button className="eex-btn-nav eex-btn-nav--next" onClick={() => goToFlat(myFlat + 1)}>{t.next}</button>
                            : <button className="eex-btn-nav eex-btn-nav--done" onClick={() => setShowGeneral(true)}>{t.done}</button>
                          }
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {saveFlash && <div className="eex-save-flash">{t.saved}</div>}
    </div>
  );
}